import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, Mail, ShieldCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex relative bg-primary text-primary-foreground p-12 flex-col justify-between overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-10" />
        <Link to="/" className="relative flex items-center gap-2.5">
          <div className="grid h-10 w-10 place-items-center rounded-md bg-primary-foreground text-primary font-display font-bold">R</div>
          <div className="font-display text-lg font-bold tracking-tight">CA Ritika Singh</div>
        </Link>
        <div className="relative max-w-md">
          <ShieldCheck className="h-10 w-10 mb-6 text-accent" />
          <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight leading-tight">
            Account recovery is secure and takes less than a minute.
          </h2>
          <p className="mt-4 text-primary-foreground/75 leading-relaxed">
            We'll send a one-time reset link to your registered email address. It expires in 1 hour.
          </p>
        </div>
        <div className="relative text-xs text-primary-foreground/60">© {new Date().getFullYear()} CA Ritika Singh · Made in India</div>
      </div>

      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          {sent ? (
            <div className="text-center">
              <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-accent/10 text-accent mb-5">
                <CheckCircle2 className="h-7 w-7" />
              </div>
              <h1 className="font-display text-2xl font-bold tracking-tight">Check your inbox</h1>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                If <strong className="text-foreground">{email}</strong> is registered, a password reset link is on its way. Check your spam folder if you don't see it within a few minutes.
              </p>
              <p className="mt-6 text-sm text-muted-foreground">
                <Link to="/login" className="font-semibold text-foreground hover:underline">← Back to sign in</Link>
              </p>
            </div>
          ) : (
            <>
              <h1 className="font-display text-3xl font-bold tracking-tight">Forgot password?</h1>
              <p className="mt-2 text-sm text-muted-foreground">Enter your email and we'll send a reset link.</p>

              <form onSubmit={onSubmit} className="mt-8 space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <div className="relative mt-1.5">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="h-11 pl-9"
                      placeholder="you@example.com"
                    />
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full h-11">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset link"}
                </Button>
              </form>

              <p className="mt-6 text-sm text-muted-foreground">
                Remembered it? <Link to="/login" className="font-semibold text-foreground hover:underline">Sign in</Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
